import { AimOutlined } from '@ant-design/icons';
import type React from 'react';
import { useInsertionEffect } from 'react';
import { getStatusColor } from '@/core/utils/get_status_color';
import {
  GEO_EVENT_HIGHLIGHT_BORDER_WIDTH,
  GEO_EVENT_HIGHLIGHT_ICON_SIZE,
  GEO_EVENT_HIGHLIGHT_PULSE_COLOR,
  GEO_EVENT_HIGHLIGHT_PULSE_DURATION_MS,
  GEO_EVENT_HIGHLIGHT_SIZE,
  GEO_EVENT_HIGHLIGHT_Z_INDEX,
} from '../constants';

const PULSE_KEYFRAMES_ID = 'geo-event-pulse-keyframes';

const PULSE_KEYFRAMES_CSS = `
@keyframes geo-event-pulse {
  0% { box-shadow: 0 0 0 0 ${GEO_EVENT_HIGHLIGHT_PULSE_COLOR}99; }
  70% { box-shadow: 0 0 0 16px ${GEO_EVENT_HIGHLIGHT_PULSE_COLOR}00; }
  100% { box-shadow: 0 0 0 0 ${GEO_EVENT_HIGHLIGHT_PULSE_COLOR}00; }
}
`;

function ensurePulseKeyframesInjected(): void {
  if (typeof document === 'undefined') return;
  if (document.getElementById(PULSE_KEYFRAMES_ID)) return;
  const style = document.createElement('style');
  style.id = PULSE_KEYFRAMES_ID;
  style.textContent = PULSE_KEYFRAMES_CSS;
  document.head.appendChild(style);
}

export interface GeoEventHighlightMarkerProps {
  lat: number;
  lng: number;
  statusName: string;
}

export const GeoEventHighlightMarker: React.FC<GeoEventHighlightMarkerProps> = ({ statusName }) => {
  useInsertionEffect(() => {
    ensurePulseKeyframesInjected();
  }, []);

  const statusColor = getStatusColor(statusName);

  return (
    <div
      role="img"
      aria-label={`Geo event: ${statusName}`}
      data-testid="geo-event-highlight-marker"
      style={{
        transform: 'translate(-50%, -50%)',
        width: GEO_EVENT_HIGHLIGHT_SIZE,
        height: GEO_EVENT_HIGHLIGHT_SIZE,
        borderRadius: '50%',
        background: statusColor,
        border: `${GEO_EVENT_HIGHLIGHT_BORDER_WIDTH}px solid #fff`,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        animation: `geo-event-pulse ${GEO_EVENT_HIGHLIGHT_PULSE_DURATION_MS}ms ease-out infinite`,
        zIndex: GEO_EVENT_HIGHLIGHT_Z_INDEX,
        cursor: 'default',
      }}
    >
      <AimOutlined style={{ fontSize: GEO_EVENT_HIGHLIGHT_ICON_SIZE, color: '#fff' }} />
    </div>
  );
};
